import React, { useEffect, useState } from 'react';
import SectionHeader from './SectionHeader';
import YesNoRadio from './YesNoRadio';
import NotesInput from './NotesInput';

function filterContactPrefill(prefill) {
  if (!prefill || typeof prefill !== 'object') {
    return {};
  }

  const next = {};
  const name = String(prefill.name || prefill.tamarac_display_name || '').trim();
  const email = String(prefill.email || '').trim();
  const phone = String(prefill.phone || '').trim();
  const partnerFirst = String(prefill.partner_first_name || '').trim();
  const partnerLast = String(prefill.partner_last_name || '').trim();
  const partnerName = [partnerFirst, partnerLast].filter(Boolean).join(' ').trim();

  if (name) next.name = name;
  if (email) next.email = email;
  if (phone) next.phone = phone;

  const hasPartner = String(prefill.has_partner || '').trim().toLowerCase() === 'yes'
    || partnerName.length > 0;
  if (hasPartner) {
    next.shares_finances = 'yes';
    if (partnerName) next.partner_name = partnerName;
  }

  return next;
}

/**
 * Section 1 — Contact Information.
 *
 * Confirms name / email / phone, and asks whether the client shares
 * finances with a partner or spouse (capturing the partner's name when
 * applicable).
 */
export default function StepContact({ data, prefill, onChange }) {
  const [hasMerged, setHasMerged] = useState(false);

  useEffect(() => {
    if (hasMerged) return;
    if (!prefill) {
      setHasMerged(true);
      return;
    }
    const isEmpty = !data || Object.keys(data).length === 0;
    if (isEmpty) {
      onChange(filterContactPrefill(prefill));
    }
    setHasMerged(true);
  }, [prefill, data, hasMerged, onChange]);

  function update(field, value) {
    onChange({ ...(data || {}), [field]: value });
  }

  const sharesFinances = data?.shares_finances === 'yes';

  return (
    <div>
      <SectionHeader
        number={1}
        title="Contact Information"
        tip="Please confirm your contact details so your advisor can reach you."
        showAnnualNote={false}
      />

      <div className="cashflow-section-card">
        <div className="row g-3">
          <div className="col-md-6">
            <label htmlFor="name" className="form-label">
              Name <span className="text-danger">*</span>
            </label>
            <input
              id="name"
              type="text"
              className="form-control"
              value={data?.name || ''}
              onChange={(e) => update('name', e.target.value)}
            />
          </div>
          <div className="col-md-6">
            <label htmlFor="email" className="form-label">
              Email <span className="text-danger">*</span>
            </label>
            <input
              id="email"
              type="email"
              className="form-control"
              value={data?.email || ''}
              onChange={(e) => update('email', e.target.value)}
            />
          </div>
          <div className="col-md-6">
            <label htmlFor="phone" className="form-label">
              What is the best phone number to reach you? <span className="text-danger">*</span>
            </label>
            <input
              id="phone"
              type="tel"
              className="form-control"
              value={data?.phone || ''}
              onChange={(e) => update('phone', e.target.value)}
            />
          </div>
        </div>

        <hr className="my-3" />

        <YesNoRadio
          id="shares_finances"
          label={<>Do you share finances with a partner or spouse? <span className="text-danger">*</span></>}
          value={data?.shares_finances}
          onChange={(val) => update('shares_finances', val)}
        />

        {sharesFinances && (
          <div className="mt-2">
            <label htmlFor="partner_name" className="form-label">Partner / Spouse Name</label>
            <input
              id="partner_name"
              type="text"
              className="form-control"
              value={data?.partner_name || ''}
              onChange={(e) => update('partner_name', e.target.value)}
            />
          </div>
        )}
      </div>

      <NotesInput
        value={data?.notes}
        onChange={(val) => update('notes', val)}
      />
    </div>
  );
}
